import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { isWorkshopBffEnabled } from "@/env";
import {
  DocsConflictError,
  getJsonWithEtag,
  isDocsAuthReady,
  putJsonWithEtag,
} from "@/lib/docs-api";

/**
 * Shared-persistence helper for the **single-document** stores — the ones
 * whose state is a flat object / map / list, not the per-workshop record
 * buckets {@link createWorkshopRecordStore} owns. `useProgress` (three status
 * maps), `useLinks` (a flat link graph) and `useWorkshopSummaries` (a
 * workshopId→summary map) all share the same durability mechanics but each has
 * a different shape + merge policy, so this module owns the transport +
 * IndexedDB-cache + resilience and the caller supplies `parse` and `merge`.
 *
 * Backend posture mirrors the record-store factory exactly:
 *   - **idb-only (default / offline / tests):** every helper is a pure IDB
 *     read/write — behaviour is byte-identical to the hand-rolled stores these
 *     replaced, so existing tests stay green and air-gapped use is unaffected.
 *   - **bff (`VITE_WORKSHOP_BACKEND=bff`):** the document rides the generic
 *     `/api/docs/workshops/file` blob endpoint as one JSON blob under
 *     `_program/<name>.json`. No BFF code change — the endpoint authorizes on
 *     the `workshops` tier, not the path (a new doc is just a new path prefix,
 *     same as `useWorkshopOverrides`). IndexedDB stays as the optimistic +
 *     offline cache: writes go local-first then fire-and-forget to the BFF;
 *     hydrate/sync prefer the shared copy and degrade to the cache on outage.
 *
 * Concurrency (E1): whole-document writes are guarded by the BFF's optimistic
 * concurrency layer. Every hydrate/sync pull captures the document's ETag (in
 * {@link etagCache}, keyed by blob path); a write sends it back as `If-Match`
 * so a write racing another writer fails with a 412 ({@link DocsConflictError})
 * instead of silently clobbering. On 412 we re-pull, re-run the store's `merge`
 * (recency for the singletons, id-union for the link list — the same hook that
 * reconciles a poll mid-edit), and retry once with the fresh ETag, so disjoint
 * concurrent edits converge automatically. A never-written document is created
 * with `If-None-Match: *` so two first-writers can't both think they created it.
 */

/** The BFF tier these program-scoped documents live under. */
const WORKSHOPS_TIER = "workshops";

/** Mirrors `SyncState` in `@/stores/synced-stores` — duplicated to avoid an
 *  import cycle (synced-stores imports the stores that import this). */
export type SyncState = "idle" | "syncing" | "synced" | "error" | "disabled";

/**
 * The sync slice every single-document store exposes so it can register in
 * `SYNCED_STORES` and be driven by the 15s / focus / cross-tab poll. Identical
 * shape to the record-store factory's slice.
 */
export type StateDocSyncSlice = {
  syncState: SyncState;
  lastSyncedAt: number | null;
  syncError: string | null;
  sync: () => Promise<void>;
};

/** `"disabled"` until a BFF backend is configured — matches the factory + keeps
 *  the header sync pill hidden in the idb-only posture. */
export const initialSyncState = (): SyncState =>
  isWorkshopBffEnabled() ? "idle" : "disabled";

/**
 * Last-seen ETag per blob path (E1). Populated on every pull, consumed as the
 * next write's `If-Match`. Module-scoped so the stateless helpers share it
 * without changing any store's shape; a path absent from the map means "never
 * pulled this session" → the write is create-only (`If-None-Match: *`).
 */
const etagCache = new Map<string, string | null>();

/** Read the durable IDB cache (never throws). */
async function readCache(idbKey: string): Promise<unknown> {
  try {
    return await idbGet(idbKey);
  } catch {
    return undefined;
  }
}

/** Best-effort IDB write (never throws — see the factory's posture). */
async function cacheToIdb(idbKey: string, value: unknown): Promise<void> {
  try {
    await idbSet(idbKey, value);
  } catch {
    /* best-effort durability */
  }
}

type DocOptions<T> = {
  /** idb-keyval key the document persists under locally. */
  idbKey: string;
  /** Blob path within the `workshops` tier (e.g. `_program/progress.json`). */
  path: string;
  /** Validate + normalise a raw value (BFF JSON or IDB cache) into state. */
  parse: (raw: unknown) => T;
  /**
   * Reconcile a freshly-pulled shared copy (`remote`, `null` when the blob was
   * never written) with the local copy. Recency for singletons; id-union for
   * the link list. Must be pure.
   */
  merge: (remote: T | null, local: T) => T;
};

/**
 * BFF-first hydrate. Reads the IDB cache first (instant / offline), then —
 * when the BFF backend is on — pulls the shared blob and reconciles via
 * `merge`. Never throws: a total outage degrades to the cache and reports a
 * soft `loadError` the caller can surface.
 */
export async function hydrateStateDoc<T>(
  opts: DocOptions<T>,
): Promise<{ value: T; loadError: string | null }> {
  const cached = opts.parse(await readCache(opts.idbKey));
  if (!isWorkshopBffEnabled()) return { value: cached, loadError: null };
  try {
    const { value: remoteRaw, etag } = await getJsonWithEtag<unknown>(
      WORKSHOPS_TIER,
      opts.path,
    );
    etagCache.set(opts.path, etag);
    const remote = remoteRaw == null ? null : opts.parse(remoteRaw);
    const merged = opts.merge(remote, cached);
    void cacheToIdb(opts.idbKey, merged);
    return { value: merged, loadError: null };
  } catch {
    return {
      value: cached,
      loadError: "Couldn’t reach the records service — showing your local copy.",
    };
  }
}

/**
 * Force a re-pull + reconcile (drives the `SYNCED_STORES` poll + the manual
 * sync pill). No-op (`disabled`) without a BFF backend. Never throws.
 */
export async function syncStateDoc<T>(
  opts: DocOptions<T> & { local: T },
): Promise<{ value: T; state: SyncState; error: string | null }> {
  if (!isWorkshopBffEnabled()) {
    return { value: opts.local, state: "disabled", error: null };
  }
  // Soft-skip before OIDC is ready — same boot/login race as Asset Locker.
  if (!(await isDocsAuthReady())) {
    return { value: opts.local, state: "idle", error: null };
  }
  try {
    const { value: remoteRaw, etag } = await getJsonWithEtag<unknown>(
      WORKSHOPS_TIER,
      opts.path,
    );
    etagCache.set(opts.path, etag);
    const remote = remoteRaw == null ? null : opts.parse(remoteRaw);
    const merged = opts.merge(remote, opts.local);
    void cacheToIdb(opts.idbKey, merged);
    return { value: merged, state: "synced", error: null };
  } catch {
    return { value: opts.local, state: "error", error: "Couldn’t reach the records service." };
  }
}

/**
 * Conditional write of a whole document with E1 concurrency control + a single
 * conflict-resolving retry. Picks the precondition from {@link etagCache}:
 *   - a string etag → `If-Match` (overwrite only if unchanged)
 *   - never pulled (`undefined`) → `If-None-Match: *` (create-only)
 *   - pulled but no etag exposed (`null`) → blind write (older backend)
 *
 * On a 412 ({@link DocsConflictError}) it re-pulls, re-runs `merge` to fold the
 * other writer's copy into `value`, and retries once with the fresh etag — so
 * disjoint concurrent edits converge without surfacing to the user. Returns the
 * value actually written (the merged one after a reconcile), or `null` if the
 * write ultimately failed (caller keeps its optimistic local copy; the next
 * sync reconciles). Never throws.
 */
async function conditionalWrite<T>(
  path: string,
  value: T,
  parse: (raw: unknown) => T,
  merge: (remote: T | null, local: T) => T,
): Promise<T | null> {
  const seen = etagCache.get(path); // undefined = never pulled this session
  const precondition =
    typeof seen === "string"
      ? { ifMatch: seen }
      : seen === undefined
        ? { createOnly: true }
        : {}; // null → no etag available; fall back to a blind write
  try {
    const { etag } = await putJsonWithEtag(WORKSHOPS_TIER, path, value, precondition);
    etagCache.set(path, etag);
    return value;
  } catch (err) {
    if (!(err instanceof DocsConflictError)) return null; // transient — swallow
    // Lost-update race: re-pull, fold the other writer's copy in, retry once.
    try {
      const { value: remoteRaw, etag } = await getJsonWithEtag<unknown>(
        WORKSHOPS_TIER,
        path,
      );
      etagCache.set(path, etag);
      const remote = remoteRaw == null ? null : parse(remoteRaw);
      const merged = merge(remote, value);
      const { etag: newEtag } = await putJsonWithEtag(WORKSHOPS_TIER, path, merged, {
        ifMatch: etag ?? undefined,
      });
      etagCache.set(path, newEtag);
      return merged;
    } catch {
      return null; // still conflicted — leave it to the next sync
    }
  }
}

type PersistOptions<T> = {
  /** idb-keyval key the document persists under locally. */
  idbKey: string;
  /** Blob path within the `workshops` tier. */
  path: string;
  /** The new local value to persist. */
  value: T;
  /** Same `parse`/`merge` the store hands hydrate/sync — reused to fold in a
   *  racing writer's copy on a 412. */
  parse: (raw: unknown) => T;
  merge: (remote: T | null, local: T) => T;
  /**
   * Called with the reconciled value when a 412 forced a re-merge that differs
   * from what the store currently holds, so the store can adopt it into memory.
   * Not called on the happy path (no conflict). Optional — a store that doesn't
   * care just lets the next sync pick up the merged copy.
   */
  onReconciled?: (value: T) => void;
};

/**
 * Persist the whole document: IDB cache always, plus an optimistic conditional
 * shared write (E1) when the BFF backend is on. On a concurrent-edit 412 it
 * re-pulls + merges + retries (see {@link conditionalWrite}) and calls
 * `onReconciled` with the merged value so the store can adopt it.
 */
export function persistStateDoc<T>(opts: PersistOptions<T>): void {
  void cacheToIdb(opts.idbKey, opts.value);
  if (!isWorkshopBffEnabled()) return;
  void conditionalWrite(opts.path, opts.value, opts.parse, opts.merge).then(
    (written) => {
      if (written == null) return;
      void cacheToIdb(opts.idbKey, written);
      // Only signal the store when a reconcile actually changed the value —
      // the happy path returns the same reference we passed in.
      if (written !== opts.value) opts.onReconciled?.(written);
    },
  );
}

/**
 * Awaited one-time push of the current local document to the shared backend —
 * the single-document analogue of the record-store factory's `pushLocalToBff`,
 * used by the IDB→BFF cutover migration. No-op (returns false) when the BFF
 * backend is disabled. Goes through {@link conditionalWrite} so a migration
 * racing a live edit merges rather than clobbers.
 */
export async function pushStateDoc<T>(
  path: string,
  value: T,
  parse: (raw: unknown) => T,
  merge: (remote: T | null, local: T) => T,
): Promise<boolean> {
  if (!isWorkshopBffEnabled()) return false;
  const written = await conditionalWrite(path, value, parse, merge);
  return written != null;
}
